import { EditorDefns } from "../../../utility";
import * as ARS from "../../ars_ldm";
import { PLACEHOLDERGROUPINGFACTOR } from "../../sidebar/TotalGroupingFactorSidebarItem";
import {
  ArsTableGroupCalculationDto,
  ArsTableOperation,
  ArsTableSubGroup,
  ArsTableSubGroupGroup,
  ArsTableSubGroupGroupingFactor,
} from "../interface";
import { isArsTableOperation, isArsTableSubGroupGroupingFactor } from "../typeguards";

/**
 * Maps ARS grouping factors, groups, and operations to their corresponding table group types for use in the TLF editor.
 *
 * @param group - ARS Group to map to a table group
 * @param editorProps - Editor definitions for text translation and type checking
 * @returns ArsTableSubGroupGroup - Mapped table group
 */
export const arst_groupToTableGroup = <T>(
  group: ARS.Group,
  editorProps: EditorDefns<T>,
): ArsTableSubGroupGroup<T> => ({
  id: crypto.randomUUID(),
  label: editorProps.textToT(group.label?.replaceAll("~", "\n") ?? group.name),
  group: group,
  groupId: group.id,
});

/**
 * Maps an ARS grouping factor to a table subgroup of type grouping factor,
 * including recursive mapping of any nested groups.
 *
 * @param groupingFactor - ARS GroupingFactor to map to a table subgroup
 * @param editorProps - Editor definitions for text translation and type checking
 * @returns ArsTableSubGroupGroupingFactor - Mapped table subgroup
 */
export const arst_groupingFactorToTableGroup = <T>(
  groupingFactor: ARS.GroupingFactor,
  editorProps: EditorDefns<T>,
): ArsTableSubGroupGroupingFactor<T> => ({
  id: crypto.randomUUID(),
  groupingFactor: groupingFactor,
  groupingFactorId: groupingFactor.id,
  label: editorProps.textToT(groupingFactor.label?.replaceAll("~", "\n") ?? groupingFactor.name),
  isPlaceholder: groupingFactor.name === PLACEHOLDERGROUPINGFACTOR,
});

/**
 * Maps an ARS operation to a table group of type operation.
 *
 * @param operation - ARS Operation to map to a table group
 * @param editorProps - Editor definitions for text translation and type checking
 * @returns ArsTableOperation - Mapped table operation group
 */
export const arst_operationToTableGroup = <T>(
  operation: ARS.Operation,
  editorProps: EditorDefns<T>,
): ArsTableOperation<T> => ({
  id: crypto.randomUUID(),
  label: editorProps.textToT(operation.label?.replaceAll("~", "\n") ?? operation.name),
  operation,
  operationId: operation.id,
  targetVariable: "",
});

/**
 * Maps an ARS analysis method to a table subgroup of type analysis method, including recursive mapping of its operations.
 *
 * @param analysisMethod - ARS AnalysisMethod to map to a table subgroup
 * @param editorProps - Editor definitions for text translation and type checking
 * @returns ArsTableSubGroupAnalysisMethod - Mapped table subgroup for the analysis method
 */
export const arst_analysisMethodToTableGroup = <T>(
  analysisMethod: ARS.AnalysisMethod,
  editorProps: EditorDefns<T>,
): ArsTableSubGroup<T> => ({
  id: crypto.randomUUID(),
  label: editorProps.textToT(analysisMethod.label?.replaceAll("~", "\n") ?? analysisMethod.name),
  analysisMethod,
  analysisMethodId: analysisMethod.id,
  subGroups: analysisMethod.operations.map((operation) =>
    arst_operationToTableGroup(operation, editorProps),
  ),
});

/**
 * Recursively calculates the depth of an ARS table group structure,
 * counting grouping factors with data-driven groups as double depth and excluding hidden operations.
 *
 * @param group - ARS table group to calculate depth for
 * @param editorProps - Editor definitions for text translation and type checking
 * @returns number - Calculated depth of the table group
 */
export const arst_tableGroupDepth = <T>(
  group: ArsTableGroupCalculationDto<T>,
  editorProps: EditorDefns<T>,
): number => {
  const subGroups = "subGroups" in group && group.subGroups ? group.subGroups : [];
  const subGroupDepth = Math.max(
    ...subGroups.map((subGroup) => arst_tableGroupDepth(subGroup, editorProps)),
    0,
  );
  let thisDepth =
    isArsTableSubGroupGroupingFactor(group, editorProps.isT) &&
    !group.hidden &&
    ((group.groupingFactor.dataDriven && group.groupingFactor.dataValues.length > 0) ||
      (!group.groupingFactor.dataDriven && group.groupingFactor.groups.length > 0))
      ? 2
      : isArsTableOperation(group, editorProps.isT) && group.hidden
        ? 0
        : 1;
  if (group.inlineWithPreviousLevel) thisDepth = thisDepth - 1;
  return thisDepth + subGroupDepth;
};
